import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouteState } from "../../hooks/useRouteState";
import { useAppState } from "../../AppContext";
import {
  Hotspot,
  PanoScenario,
  Pano as PanoType,
  UseStateReturn,
  PanoAction,
  PanoActionTypeMap,
  PanoHotspotTypeMap,
} from "../../types";
import classNames from "classnames";
import { MarkerTypes } from "../../shared/components/MarkerImages";
import { imagePanoXml, customPanoXml, videoPanoXml } from "../../utils/panoXmlUtils";
import { PanoScenarioSwitch } from "./PanoScenarioSwitch";
import {
  PanoContainer,
  PanoCloseLink,
  CloseIconSpan,
  PanoFade,
  PanoMapWrapper,
  PanoExcerpt,
} from "./PanoStyles";
import { ReactComponent as CloseIcon } from "../../images/close.svg";
import { ReactComponent as VrIcon } from "../../images/vr.svg";
import { ReactComponent as BubbleIcon } from "../../images/bubble.svg";
import { ReactComponent as ShareIcon } from "../../images/share.svg";
import { ReactComponent as MapIcon } from "../../images/map.svg";
import { ReactComponent as EnableGyroIcon } from "../../images/enable-gyro.svg";
import { ReactComponent as DisableGyroIcon } from "../../images/disable-gyro.svg";
import ActionList from "../../shared/components/ActionList";
import Action from "../../shared/components/Action";
import { MemoizedWordpressContentWrapper } from "../../shared/components/TextWrapper";
import { Route, useHistory } from "react-router-dom";
import { PanoVideoControls } from "./PanoVideoControls";
import { isPanoTourPoint } from "../../utils/tourUtils";
import { shareView } from "../../utils/sharedUtils";
import { ShareModal } from "../Modals/ShareModal";
import { useInterfaceState } from "../../InterfaceContext";
import { isPanoFaq } from "../../utils/faqUtils";
import { usePanoUrl } from "../../hooks/usePanoUrl";
import { usePoiUrl } from "../../hooks/usePoiUrl";
import { useQuestionUrl } from "../../hooks/useQuestionUrl";
import { usePanoState } from "../../hooks/usePanoState";
import { useSetPanoState } from "../../hooks/useSetPanoState";
import { MiniMap } from "./MiniMap";
import IsDesktop from "../../shared/components/IsDeskop";
import { useUrlState } from "../../hooks/useUrlState";
import { Capability, useCapability } from "../../hooks/useCapability";
import { getTokenValue } from "../../TokenStyles";
import { PanoMap } from "./PanoMap";
import IsMobile from "../../shared/components/IsMobile";
import { useMapUrl } from "../../hooks/useMapUrl";
import { PanoActions } from "./PanoActions";
import { useContentUrl } from "../../hooks/useContentUrl";
import {
  getPanoIcon,
  getPoiIcon,
  isOpenThreeSceneHotspot,
  isOpenUrlHotspot,
  isPointHotspot,
  isPolygonHotspot,
  isPolylineHotspot,
  isSwitchScenarioHotspot,
  krpanoId,
  removeHotspot,
} from "../../utils/panoUtils";
import { PanoAudioControls } from "./PanoAudioControls";
import { useSessionStorage } from "../../hooks/useStorage";
import { useFeature, Feature } from "../../hooks/useFeature";
import Skeleton from "react-loading-skeleton";
import { SkeletonContainer } from "../../AppStyles";
import { useUpdateStateUrl } from "../../hooks/useUpdateStateUrl";
import { messages } from "../../global-intl-messages";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import { makeKeyDownHandler } from "../../utils/a11y-utils";
import { isLocalUrl, mergeSearchParams } from "../../utils/urlUtils";
import { ThreeViewer } from "../Three/ThreeViewer";
import { SceneHistory, useSceneUrl } from "../../hooks/useSceneUrl";
import { KrpanoElement } from "../../libs/krpano";
import {
  isMapView,
  isPanoramaView,
  useSurveyState,
} from "../../SurveyContext";
import { useCopy } from "../../hooks/useCopy";

const Krpano = React.lazy(() => import("./Krpano"));
const krpanoZoomFactor = 1.5;
const mobileDevices = ["Android", "iPhone"];

export default function Pano() {
  const routeState = useRouteState(true);
  const setPanoState = useSetPanoState();
  const { state } = useAppState();
  const copy = useCopy();
  const { interfaceState, setInterfaceState, isInVr, participationState } = useInterfaceState();
  const { surveyState } = useSurveyState();
  const urlState = useUrlState();
  const prevPano = useRef<any>();
  const shouldGoBack = useRef(false);
  const [switchScenario, setSwitchScenario] = useState<string | null>(null);
  const history = useHistory();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const panoUrl = usePanoUrl();
  const poiUrl = usePoiUrl();
  const contentUrl = useContentUrl();
  const questionUrl = useQuestionUrl();
  const mapUrl = useMapUrl();
  const updateStateUrl = useUpdateStateUrl();
  const sceneUrl = useSceneUrl();
  const panoState = usePanoState();
  const [loadedPano] = useSessionStorage("loadedPano");
  const [disablePanoScenarioTeaser, setDisablePanoScenarioTeaser] = useSessionStorage(
    "disablePanoScenarioTeaser"
  );
  const participating = Boolean(routeState.participationItemSlug);
  const [faded, setFaded] = useState(false);
  const [teasing, setTeasing] = useState(false);
  const [gyroActive, setGyroActive] = useState(false);
  const [sidebarWasOpen] = useState(interfaceState.sidebarOpen);
  const shareAvailable = useFeature(Feature.Sharing);
  const askQuestionAvailable = useFeature(Feature.AskQuestion);
  const vrAvailable = useFeature(Feature.VR);
  const iOsVrAvailable = useFeature(Feature.IOS_VR);
  const intl = useIntl();

  const krpano = document.getElementById(krpanoId) as KrpanoElement | undefined;

  const showFade = useMemo(() => {
    return loadedPano === null && interfaceState.currentEntrance.type !== "map";
  }, [loadedPano, interfaceState.currentEntrance.type]);

  const scenarioSlug = routeState.scenarioSlug || state.defaultScenarioSlug;
  const { tourSlug, tourPointSlug } = routeState;

  const tour = useMemo(
    () => state.tours.find((tour) => tour.slug === tourSlug) ?? null,
    [state.tours, tourSlug]
  );

  const tourPoint = useMemo(
    () => tour?.points.find((point) => point.slug === tourPointSlug),
    [tourPointSlug, tour?.points]
  );

  let panoScenario: PanoScenario | undefined;
  let pano: PanoType | undefined;

  const { mapView, panoramaView } = useMemo(() => {
    const view = surveyState.activePage?.initialView;

    return {
      mapView: isMapView(view) ? view : null,
      panoramaView: isPanoramaView(view) ? view : null,
    };
  }, [surveyState.activePage?.initialView]);

  if (tourPointSlug) {
    if (tourPoint && isPanoTourPoint(tourPoint)) {
      pano = state.panos.find((pano) => {
        const match = pano.slug === tourPoint.data.pano.slug;
        if (match) {
          panoScenario = pano.scenarios.find(
            (scenario) => scenario.slug === tourPoint.scenarioSlug
          );
          return !!panoScenario;
        }
        return false;
      });
    }
  } else if (participationState.panoSlug && !participationState.navigatedPano) {
    pano = state.panos.find((p) => {
      const match = p.slug === participationState.panoSlug;
      if (match) {
        panoScenario =
          p.scenarios.find((scenario) => scenario.slug === participationState.scenarioSlug) ||
          p.scenarios.find((scenario) => scenario.slug === scenarioSlug) ||
          p.scenarios[0];
        return !!panoScenario;
      }
      return false;
    });
  } else {
    pano = state.panos.find((pano) => {
      const match = pano.slug === routeState.panoSlug;
      if (match) {
        panoScenario =
          (participationState.scenarioSlug
            ? pano.scenarios.find((scenario) => scenario.slug === participationState.scenarioSlug)
            : pano.scenarios.find((scenario) => scenario.slug === scenarioSlug)) ||
          pano.scenarios[0];
        return !!panoScenario;
      }
      return false;
    });
  }

  const storeScenarioSwitched = useCallback(
    (slug: string) => {
      if (routeState.activeScenarioSlug !== slug && disablePanoScenarioTeaser === null)
        setDisablePanoScenarioTeaser("true");
    },
    [routeState.activeScenarioSlug, disablePanoScenarioTeaser, setDisablePanoScenarioTeaser]
  );

  useEffect(() => {
    if (switchScenario) {
      storeScenarioSwitched(switchScenario);
      setSwitchScenario(null);
      history.push(updateStateUrl({ scenarioSlug: switchScenario }, contentUrl(), true));
    }
  }, [
    history,
    contentUrl,
    switchScenario,
    setSwitchScenario,
    updateStateUrl,
    storeScenarioSwitched,
  ]);

  useEffect(() => {
    prevPano.current = pano?.slug;
    switch (true) {
      case interfaceState.currentEntrance.type === "map":
        setInterfaceState({ ...interfaceState, sidebarOpen: false });
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interfaceState.currentEntrance.type]);

  useEffect(() => {
    return () =>
      setInterfaceState((currentState) => ({ ...currentState, sidebarOpen: sidebarWasOpen }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Register escape key
    if (prevPano.current !== pano?.slug) shouldGoBack.current = true;
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (krpano?.get("webvr.isenabled")) {
          krpano.call("webvr.exitvr()");
        } else if (pano?.returnAtEndOfVideo && shouldGoBack.current) {
          history.goBack();
        } else if (!urlState.embed && interfaceState.currentEntrance.type === "map") {
          history.push(mapUrl());
        }
      }
    };
    window.addEventListener("keydown", handleEscapeKey, false);
    return () => window.removeEventListener("keydown", handleEscapeKey, false);
  }, [mapUrl, pano, urlState.embed, history, krpano, interfaceState.currentEntrance.type]);

  const urlSwitch = useCallback(
    (type: MarkerTypes, slug: string) => {
      switch (type) {
        case MarkerTypes.Pano:
          return panoUrl(slug);
        case MarkerTypes.Faq:
          return poiUrl(slug);
        case MarkerTypes.Info:
        case MarkerTypes.Question:
        case MarkerTypes.Default:
        default:
          return contentUrl(slug);
      }
    },
    [contentUrl, panoUrl, poiUrl]
  );

  const panoLinkHotspots = useMemo(() => {
    const hotspots: Hotspot[] = [];

    pano?.panoLinks?.forEach((panoLink, i) => {
      const pano = state.panos.find((p) => p.id === panoLink.id);
      const scenario = pano?.scenarios.find((s) => s.id === panoScenario?.id);
      if (pano && panoScenario && (scenario || state.showPhantomPanoLinks)) {
        const iconType = pano.icon === "auto" ? (scenario?.isVideo ? "video" : "pano") : pano.icon;

        hotspots.push({
          slug: `${pano.slug}-${i}`,
          coordinates: panoLink.coordinates,
          type: panoLink.type === "point" ? pano.type : panoLink.type,
          url: scenario && panoUrl(panoLink.slug),
          title: isPointHotspot(panoLink) ? pano.title : "",
          icon: isPointHotspot(panoLink)
            ? getPanoIcon(
                pano.type,
                iconType,
                getTokenValue(
                  scenario ? panoLink.color ?? "--color-primary-50" : "--color-neutral-50"
                )
              )
            : null,
          border:
            isPolygonHotspot(panoLink) || isPolylineHotspot(panoLink)
              ? {
                  width: panoLink.border.width,
                  color: scenario ? panoLink.border.color : getTokenValue("--color-neutral-50"),
                  opacity: scenario ? panoLink.border.opacity : 0.5,
                }
              : undefined,
          fill: isPolygonHotspot(panoLink)
            ? scenario
              ? panoLink.fill
              : { color: "--color-neutral-50", opacity: 0.5 }
            : undefined,
          allowHighlight: isPolygonHotspot(panoLink) ? panoLink.allowHighlight : true,
          onClick: scenario
            ? undefined
            : () => setInterfaceState((state) => ({ ...state, phantomPanoAlertOpen: true })),
          phantom: !scenario,
        });
      }
    });

    return hotspots;
  }, [pano, panoScenario, panoUrl, state.panos, state.showPhantomPanoLinks, setInterfaceState]);

  const poiHotspots = useMemo(() => {
    const hotspots: Hotspot[] = [];

    panoScenario?.pois.forEach((poi, i) => {
      const scenario = pano?.scenarios.find((s) => s.slug === panoScenario?.slug);
      if (pano && scenario) {
        hotspots.push({
          title: poi.coordinates.length > 1 || !state.showPoiLabels ? "" : poi.title,
          slug: `${poi.slug}-${i}`,
          type: poi.hotspotType === "point" ? undefined : poi.hotspotType,
          coordinates: poi.coordinates,
          excerpt: `<h2>${poi.title}</h3>${poi.excerpt}`,
          icon: getPoiIcon(poi.type as MarkerTypes, isPointHotspot(poi) ? poi.color : undefined),
          url: urlSwitch(poi.type as MarkerTypes, poi.slug),
          onClick: () => {
            setTimeout(
              () =>
                krpano
                  ?.get("hotspot")
                  .getArray()
                  .filter((h: any) => new RegExp(`${poi.slug}-\\d+-badge`).test(h.name))
                  .forEach((h: any) => removeHotspot(krpano, h.name)),
              200
            );
            poi.showBadge = false;
            history.push(urlSwitch(poi.type as MarkerTypes, poi.slug));
          },
          border: isPolygonHotspot(poi) || isPolylineHotspot(poi) ? poi.border : undefined,
          fill: isPolygonHotspot(poi) ? poi.fill : undefined,
          allowHighlight: isPolygonHotspot(poi) ? poi.allowHighlight : true,
          showBadge: !isPointHotspot(poi) && poi.showBadge,
        });
      }
    });

    return hotspots;
  }, [pano, panoScenario, urlSwitch, state.showPoiLabels, krpano, history]);

  const faqs = useMemo(
    () =>
      state.faqs
        .filter(isPanoFaq)
        .filter(
          (f) => f.scenario.slug === panoScenario?.slug && f.data.pano.slug === routeState.panoSlug
        ),
    [state.faqs, routeState.panoSlug, panoScenario?.slug]
  );

  const faqHotspots = useMemo(() => {
    const hotspots: Hotspot[] = [];

    faqs.forEach((faq, i) => {
      hotspots.push({
        title: state.showPoiLabels ? "" : faq.question,
        slug: `${faq.slug}-${i}`,
        type: "faq",
        coordinates: [faq.data.location],
        excerpt: `<h2>${faq.question}</h3>${faq.answer}`,
        icon: getPoiIcon(MarkerTypes.Faq),
        url: urlSwitch(MarkerTypes.Faq, faq.slug),
        allowHighlight: false,
      });
    });

    return hotspots;
  }, [faqs, urlSwitch, state.showPoiLabels]);

  const actionUrlSwitch = useCallback(
    (actionHotspot: PanoAction<keyof PanoActionTypeMap, keyof PanoHotspotTypeMap>) => {
      if (!krpano) return;
      krpano.set("is_hs_click", true);
      if (isSwitchScenarioHotspot(actionHotspot)) {
        setPanoState(krpano.get("view"));
        setSwitchScenario(actionHotspot.target.slug);
      } else if (isOpenUrlHotspot(actionHotspot)) {
        if (isLocalUrl(actionHotspot.targetUrl)) {
          const url = new URL(actionHotspot.targetUrl, window.location.origin);
          const currentSearch = new URLSearchParams(window.location.search);

          url.search = mergeSearchParams(currentSearch, url.searchParams).toString();

          history.push(`${url.pathname}${url.hash}${url.search}`);
        } else {
          window.open(actionHotspot.targetUrl, actionHotspot.targetWindow || "_blank", "noopener,noreferrer");
        }
      } else if (isOpenThreeSceneHotspot(actionHotspot)) {
        const historyState: SceneHistory = {
          lastPano: pano!.slug,
          lastMapOpen: !!routeState.map,
        };
        history.push(sceneUrl(actionHotspot.target), historyState);
      }
    },
    [setPanoState, setSwitchScenario, sceneUrl, krpano, history, pano, routeState.map]
  );

  const actionHotspots = useMemo(() => {
    const hotspots: (Hotspot & { action: keyof PanoActionTypeMap })[] = [];

    panoScenario?.actions.forEach((action) => {
      const scenario = pano?.scenarios.find((s) => s.slug === panoScenario?.slug);

      if (pano && scenario) {
        hotspots.push({
          title: action.label,
          slug: action.slug,
          type: action.type === "point" ? undefined : action.type,
          coordinates: action.coordinates,
          // TODO: define a default marker for actions
          icon: getPoiIcon(MarkerTypes.Edit, "--color-informative-50"),
          action: action.action,
          onClick: () => actionUrlSwitch(action),
          border: isPolygonHotspot(action) || isPolylineHotspot(action) ? action.border : undefined,
          fill: isPolygonHotspot(action) ? action.fill : undefined,
          allowHighlight: isPolygonHotspot(action) ? action.allowHighlight : true,
        });
      }
    });

    return hotspots;
  }, [pano, panoScenario, actionUrlSwitch]);

  const hotspots = useMemo(
    () => [...panoLinkHotspots, ...poiHotspots, ...faqHotspots, ...actionHotspots],
    [panoLinkHotspots, poiHotspots, faqHotspots, actionHotspots]
  );

  const vrHotspots = useMemo(
    () => [
      ...panoLinkHotspots,
      ...actionHotspots.filter((h) => ["switch-scenario"].includes(h.action)),
      ...poiHotspots,
    ],
    [panoLinkHotspots, actionHotspots, poiHotspots]
  );

  // Keep vertical orientation while in VR
  const vlookat = useMemo(() => {
    if (krpano?.get("webvr.isenabled")) return krpano.get("view.vlookat");
    return (
      panoramaView?.latitude ??
      participationState.panoLookat?.pitch ??
      panoState?.vlookat ??
      pano?.view.vlookat ??
      0
    );
  }, [pano?.view.vlookat, panoState, krpano, participationState.panoLookat?.pitch, panoramaView]);

  const hlookat = useMemo(
    () =>
      panoramaView?.longitude ??
      participationState.panoLookat?.yaw ??
      panoState?.hlookat ??
      pano?.view.hlookat ??
      0,
    [participationState.panoLookat, panoState, pano?.view.hlookat, panoramaView]
  );

  const fov = useMemo(
    () =>
      panoramaView?.fieldOfView ??
      participationState.panoLookat?.fov ??
      panoState?.fov ??
      pano?.view.fov ??
      70,
    [participationState.panoLookat, panoState, pano?.view.fov, panoramaView]
  );

  const zoomIn = useCallback(() => {
    krpano?.call(
      "tween(view.fov, " + krpano.get("view.fov") / krpanoZoomFactor + ", 0.5, easeInOutQuad )"
    );
  }, [krpano]);

  const zoomOut = useCallback(() => {
    krpano?.call(
      "tween(view.fov, " + krpano.get("view.fov") * krpanoZoomFactor + ", 0.5, easeInOutQuad )"
    );
  }, [krpano]);

  const minimapEnabledForPano = useMemo(
    () =>
      !panoramaView &&
      state.minimapEnabled &&
      pano?.latitude !== undefined &&
      pano?.longitude !== undefined,
    [state.minimapEnabled, pano?.latitude, pano?.longitude, panoramaView]
  );

  if (
    !panoramaView &&
    (!pano ||
      !panoScenario ||
      (interfaceState.currentEntrance.type !== "map" && loadedPano === undefined))
  ) {
    return null;
  }

  // @TODO Implement fade in and fade out effect
  return (
    <PanoContainer
      className={classNames({
        participating: participating,
        full: interfaceState.currentEntrance.type !== "map",
        vr: isInVr,
      })}
    >
      {!participating &&
        pano &&
        panoScenario &&
        (interfaceState.currentEntrance.type === "map" || panoScenario!.isVideo) &&
        !isInVr && (
          <PanoCloseLink
            to={{ pathname: mapUrl(), state: { latestPanoSlug: pano.slug } }}
            onClick={(e: any) => {
              if (pano!.returnAtEndOfVideo && shouldGoBack.current) {
                history.goBack();
                e.preventDefault();
              }
            }}
          >
            <CloseIconSpan>
              <CloseIcon />
            </CloseIconSpan>
          </PanoCloseLink>
        )}
      <Suspense
        fallback={
          <SkeletonContainer>
            {/** @ts-ignore */}
            <Skeleton
              circle
              width={200}
              height={200}
              style={{
                backgroundColor: "var(--color-neutral-50)",
                backgroundImage:
                  "linear-gradient( 90deg, var(--color-neutral-50),var(--color-neutral-70),var(--color-neutral-50) )",
              }}
            />
          </SkeletonContainer>
        }
      >
        <Krpano
          showFade={showFade}
          fade={[faded, setFaded] as UseStateReturn<boolean>}
          view={{
            hlookat,
            vlookat,
            fov,
          }}
          onViewChange={setPanoState}
          pano={pano}
          scenario={panoScenario}
          hotspots={panoramaView ? [] : hotspots}
          vrHotspots={panoramaView ? [] : vrHotspots}
          isVideo={!!panoScenario?.isVideo}
          showVideoControls={!!pano?.showVideoControls}
          returnAtEndOfVideo={!!pano?.returnAtEndOfVideo}
          teasing={teasing}
          setTeasing={setTeasing}
          setSwitchScenario={setSwitchScenario}
          setGyroActive={setGyroActive}
          xml={
            panoramaView
              ? customPanoXml(panoramaView.dataSource)
              : panoScenario!.isVideo
              ? videoPanoXml(pano!, panoScenario!, hotspots, state.panoramaFolder, intl)
              : imagePanoXml(pano!, panoScenario!, hotspots, state.panoramaFolder, intl)
          }
        >
          {(krpano) => (
            <>
              {!participating && (
                <>
                  {!urlState.embed && !isInVr && (
                    <PanoActions alignRight={interfaceState.currentEntrance.type !== "map"}>
                      <ActionList>
                        {shareAvailable && (
                          <>
                            <Action
                              icon={<ShareIcon />}
                              onClick={() => {
                                if (
                                  mobileDevices.some((device) =>
                                    navigator.userAgent.includes(device)
                                  ) &&
                                  navigator.share !== undefined
                                ) {
                                  shareView(
                                    state,
                                    intl.formatMessage(messages.copyShareLinkSuccessMessage),
                                    intl.formatMessage(messages.copyShareLinkFailedMessage),
                                    copy
                                  );
                                } else {
                                  setShareModalOpen(true);
                                }
                              }}
                            >
                              <FormattedMessage {...messages.shareButton} />
                            </Action>
                            <ShareModal
                              open={shareModalOpen}
                              onClose={() => setShareModalOpen(false)}
                            />
                          </>
                        )}
                        {askQuestionAvailable &&
                          state.qaSurveyId &&
                          !panoScenario?.isVideo &&
                          !urlState.embed && (
                            <Action
                              icon={<BubbleIcon />}
                              onClick={() => {
                                history.push(questionUrl(true));
                              }}
                            >
                              <FormattedMessage {...messages.askQuestionButton} />
                            </Action>
                          )}
                        {state.gyroscopeEnabled &&
                          krpano.get("plugin[gyro].isavailable") &&
                          (gyroActive ? (
                            <Action
                              icon={<DisableGyroIcon />}
                              onClick={() => krpano.set("plugin[gyro].enabled", false)}
                            >
                              <FormattedMessage
                                id="pano.disable-gyroscope"
                                defaultMessage="Disable gyroscope"
                                description="The label for the button which disables gyro mode."
                              />
                            </Action>
                          ) : (
                            <Action
                              icon={<EnableGyroIcon />}
                              onClick={() => krpano.set("plugin[gyro].enabled", true)}
                            >
                              <FormattedMessage
                                id="pano.enable-gyroscope"
                                defaultMessage="Enable gyroscope"
                                description="The label for the button which enables gyro mode."
                              />
                            </Action>
                          ))}
                        {vrAvailable && (!krpano.get("device.iphone") || iOsVrAvailable) && (
                          <Action icon={<VrIcon />} onClick={() => krpano.call(`webvr.entervr()`)}>
                            <FormattedMessage
                              id="pano.enter-vr"
                              defaultMessage="View in VR"
                              description="The label for the button which activates VR mode."
                            />
                          </Action>
                        )}
                        {interfaceState.currentEntrance.type !== "map" &&
                          state.minimapEnabled &&
                          state.map.layerGroups.length && (
                            <IsMobile>
                              <Action icon={<MapIcon />} onClick={() => history.push(mapUrl())}>
                                <FormattedMessage
                                  id="pano.view-map-button"
                                  defaultMessage="View the map"
                                  description="The label for the button which navigates to the map."
                                />
                              </Action>
                            </IsMobile>
                          )}
                      </ActionList>
                    </PanoActions>
                  )}
                  {!isInVr && pano && (
                    <>
                      <PanoScenarioSwitch pano={pano} teasing={teasing} />
                      <IsDesktop>
                        <ControlContainer
                          className={classNames("mapboxgl-ctrl mapboxgl-ctrl-group", {
                            minimap: minimapEnabledForPano,
                          })}
                        >
                          <button
                            className="mapboxgl-ctrl-icon mapboxgl-ctrl-zoom-in"
                            onClick={zoomIn}
                            onKeyDown={makeKeyDownHandler(zoomIn)}
                            aria-label={intl.formatMessage(messages.zoomIn)}
                          >
                            <span className="mapboxgl-ctrl-icon" aria-hidden="true"></span>
                          </button>
                          <button
                            className="mapboxgl-ctrl-icon mapboxgl-ctrl-zoom-out"
                            onClick={zoomOut}
                            onKeyDown={makeKeyDownHandler(zoomOut)}
                            aria-label={intl.formatMessage(messages.zoomOut)}
                          >
                            <span className="mapboxgl-ctrl-icon" aria-hidden="true"></span>
                          </button>
                        </ControlContainer>
                        {minimapEnabledForPano && <MiniMap />}
                      </IsDesktop>
                    </>
                  )}
                </>
              )}
              {krpano && (
                <>
                  {!!panoScenario?.isVideo && (
                    <PanoVideoControls
                      krpano={krpano}
                      showVideoControls={pano!.showVideoControls}
                    />
                  )}
                  {!!pano?.audio && (
                    <PanoAudioControls
                      krpano={krpano}
                      audioName={pano.slug}
                      url={pano.audio}
                      loop={pano.repeatAudio}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Krpano>
      </Suspense>
      {!!pano?.excerpt && (
        <PanoExcerpt>
          <MemoizedWordpressContentWrapper content={pano.excerpt} />
        </PanoExcerpt>
      )}
      {!!pano && (
        <>
          <Route path="/pano/*/map">
            <PanoMapWrapper>
              <PanoMap />(
              <PanoCloseLink
                to={{ pathname: panoUrl(pano.slug), state: { latestPanoSlug: pano.slug } }}
              >
                <CloseIconSpan>
                  <CloseIcon />
                </CloseIconSpan>
              </PanoCloseLink>
              )
            </PanoMapWrapper>
          </Route>
          <Route path="*/participation/:participationItemSlug">
            {participationState.layerSlug && (
              <PanoMapWrapper>
                <PanoMap />
                <PanoCloseLink
                  to={{ pathname: panoUrl(pano.slug), state: { latestPanoSlug: pano.slug } }}
                >
                  <CloseIconSpan>
                    <CloseIcon />
                  </CloseIconSpan>
                </PanoCloseLink>
              </PanoMapWrapper>
            )}
          </Route>
        </>
      )}
      {mapView && (
        <Route path="/pano/*">
          <PanoMapWrapper>
            <PanoMap />
          </PanoMapWrapper>
        </Route>
      )}
      <Route path="*/scene/:sceneSlug">
        <ThreeViewer />
      </Route>
      {showFade && (
        <PanoFade>
          <div>{state.name}</div>
        </PanoFade>
      )}
    </PanoContainer>
  );
}

const ControlContainer = styled.div`
  position: absolute;
  right: var(--space-default);
  bottom: var(--space-default);
  z-index: 25;

  .show-video-controls &,
  .show-audio-timeline & {
    margin-bottom: 48px;
  }

  &.minimap {
    bottom: calc(140px + var(--space-default) * 2);
  }
`;
